import { Chip, Typography, useTheme } from "@material-ui/core";
import {
  Activity,
  ActivityFeedItemDefinition,
} from "@ofqual/portal-activity-feed";
import React from "react";
import { useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import { fieldLabels } from "../../sections/fieldLabels";
import { ActivityType } from "../../../domain/ActivityType";
import { SocItemModel } from "../../../domain/SocItem";
import { authSelector } from "../../../redux/selectors";
import {
  AttachmentActivityBody,
  AttachmentBodyProps,
  CaseNote,
  DialogType,
  EventActivityBodyProps,
  EventCompleteActivityBodyProps,
  InternalMessage,
} from "./types";
import { DownloadLink } from "@ofqual/portal-components/components/DownloadLink";
import { socService } from "../../../services";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { EventType } from "../../../domain/EventType";
import { differenceInDays } from "date-fns";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";

import { UserType } from "@ofqual/portal-client-shell/dist/module/redux/state";

export const buildDefinitions = (
  setFocussedActivity: (activity: Activity, activityType?: DialogType) => void,
  soc: SocItemModel,
  noteTypeLookups?: ReturnType<typeof useLookup>[0],
  eventTypesLookups?: ReturnType<typeof useLookup>[0]
) => {
  const AttachmentBody: React.FC<AttachmentBodyProps> = ({ bodyProps }) => {
    const { body, activity } = bodyProps;
    const { token } = useSelector(authSelector);
   
    const buildGetBlob = (blobId: string) => () =>
      socService.downloadActivityAttachment(soc.id, activity.id, blobId, {
        token,
      });



    return (
      <>
        <ul style={{ padding: 0, marginLeft: 0, marginTop: 0, display: "flex", flexWrap: "wrap", gap: "10px" }}>
          {body.documents.map((doc) => (
            <li key={doc.blobId} style={{ listStyleType: "none", marginTop: "5px" }}>
              <DownloadLink
                fileName={doc.fileName}
                getBlob={buildGetBlob(doc.blobId)}
              />
            </li>
          ))}
        </ul>
      </>
    );
  };

  const internalMessage: ActivityFeedItemDefinition<InternalMessage> = {
    activityType: ActivityType.SocInternalMessage,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>Subject: </b>
            <Typography>{body.subject}</Typography>
            <b>Message: </b>
            <ReactMarkdown>{body.message}</ReactMarkdown>
            <b>Recipients: </b>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {body.upns.map((r) => (
                <Chip key={r} label={r} color="secondary" size="small" />
              ))}
            </div>
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
  };

  const assign: ActivityFeedItemDefinition<{}> = {
    activityType: ActivityType.SocAssign,
  };
  const unassign: ActivityFeedItemDefinition<{}> = {
    activityType: ActivityType.SocUnassign,
  };

  const noteAdded: ActivityFeedItemDefinition<CaseNote> = {
    activityType: ActivityType.SocCaseNote,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>Note type: </b>
            {(noteTypeLookups &&
              noteTypeLookups.find((x) => x.id === body.noteType)?.label) ||
              ""}
            <br />
            <b>Note: </b>
            <ReactMarkdown>{body.message}</ReactMarkdown>
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
  };

  const CloseBody: React.FC<{ reason: string }> = ({ reason }) => {
    const { userType } = useSelector(authSelector);
    return (
      <>
        {userType === UserType.Ofqual && (
          <div style={{ marginBottom: 15 }}>
            <b>Reason:</b>
            <ReactMarkdown>{reason}</ReactMarkdown>
          </div>
        )}
      </>
    );
  };

  const close: ActivityFeedItemDefinition<{ reason: string | undefined }> = {
    activityType: ActivityType.SocClose,
    Body: ({ body }) => <CloseBody reason={typeof body.reason === 'string' ? body.reason : ""} />,
  };

  const reopen: ActivityFeedItemDefinition<{}> = {
    activityType: ActivityType.SocReopen,
  };

  const messageSentOfqual: ActivityFeedItemDefinition<AttachmentActivityBody> = {
    activityType: ActivityType.SocMessageSentOfqual,
    Body: ({ body, activity }) => {
      return (
        <>
          {body.message ? (
            <div style={{ marginBottom: 15 }}>
              <b>Message: </b>
              <ReactMarkdown>{body.message}</ReactMarkdown>
            </div>
          ) : null}
          <AttachmentBody bodyProps={{ activity, body }} />
        </>
      );
    },
  };

  const messageSentAO: ActivityFeedItemDefinition<AttachmentActivityBody> = {
    activityType: ActivityType.SocMessageSentAO,
    Body: ({ body, activity }) => {
      return (
        <>
          {body.message ? (
            <div style={{ marginBottom: 15 }}>
              <b>Message: </b>
              <ReactMarkdown>{body.message}</ReactMarkdown>
            </div>
          ) : null}
          <AttachmentBody bodyProps={{ activity, body }} />
        </>
      );
    },
    align: "left",
  };

  const eventAdded: ActivityFeedItemDefinition<{}> = {
    activityType: ActivityType.SocEventAdded,
  };

  const socEvent: ActivityFeedItemDefinition<EventActivityBodyProps> = {
    activityType: EventType.NewEvent,
    Body: ({ body, activity }) => {
      const theme = useTheme();
      const meetingDate = body.meetingDate;
     
      const overdueDays =
        (meetingDate && differenceInDays(new Date(), new Date(meetingDate))) ||
        0;

      return (
        <>
          <div>
            <b>Date and time: </b>
            {formatUtcDate(meetingDate, standardDateFormats.DATE_TIME)}
          </div>
          <div>
            <b>{fieldLabels.eventType}: </b>
            <>
              { eventTypesLookups?.find((x) => x.id === body.eventType)?.label || body.eventType}
            </>
          </div>

          {overdueDays > 0 && (
            <div style={{ color: theme.palette.error.main }}>
              <b>Overdue by {overdueDays} day(s)</b>
            </div>
          )}
        </>
      );
    },
    options: [
      {
        label: "Record completion",
        onClick: (activity) => {
          setFocussedActivity(activity, DialogType.CompleteEvent);
        },
      },
    ],
  };



  const eventCompleted: ActivityFeedItemDefinition<EventCompleteActivityBodyProps> = {
    activityType: ActivityType.SocEventCompleted,
    Body: ({ body, activity }) => {
      
      const meetingDate = body.meetingDate;

      const { token } = useSelector(authSelector);

      return (
        <>
          <div>
            <b>Date and time: </b>
            {formatUtcDate(meetingDate, standardDateFormats.DATE_TIME)}
          </div>
          <div>
            <b>{fieldLabels.eventType}: </b>
            <>
              {eventTypesLookups?.find((x) => x.id === body.eventType)?.label || body.eventType}
            </>
          </div>
          <div>
            <b>Outcome: </b> Completed
          </div>
          <div>
            <b>Note: </b>
            {body.note}
          </div>
          <div>
            <ul style={{  padding: 0, marginLeft: 0, marginTop: 0, display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {body.documents.map((document: any) => (
                <li key={document.blobId} style={{ listStyleType: "none", marginTop: "5px" }}>
                  {
                     <DownloadLink
                       fileName={document.fileName}
                       getBlob={() => socService.downloadActivityAttachment(soc.id, activity.id, document.blobId, {token})}
                     />
                  }
                </li>
              ))}
            </ul>
          </div>
        </>
      );
    },
    options: [],
  };


  const socSent: ActivityFeedItemDefinition<{}> = {
    activityType: ActivityType.SocSent,
  };

  return [
    internalMessage,
    assign,
    unassign,
    noteAdded,
    close,
    reopen,
    messageSentAO,
    messageSentOfqual,
    eventAdded,
    socEvent,
    eventCompleted,
    socSent,
  ];
};